import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@vinoga/db";
import {
  Search,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 50;

type OrdersListProps = {
  /** When true, force payment_status=pending and hide that filter */
  pendingOnly?: boolean;
};

export function Orders({ pendingOnly = false }: OrdersListProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const t = useT();

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>(
    pendingOnly ? "pending" : ""
  );
  const [filterFulfilmentStore, setFilterFulfilmentStore] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);

  const fetchData = useCallback(
    async (resetPage = false) => {
      setLoading(true);
      const currentPage = resetPage ? 0 : page;
      const from = currentPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase.from("orders").select("*", { count: "exact" });

      if (search.trim()) {
        const s = search.trim();
        query = query.or(
          `order_number.ilike.%${s}%,customer_email.ilike.%${s}%,customer_first_name.ilike.%${s}%,customer_last_name.ilike.%${s}%`
        );
      }
      // The two sections are exact complements — an order is either valid or a
      // failed checkout, never both.
      //   Failed  = status=pending AND NOT bank transfer: the checkout never
      //   completed payment (abandoned or provider-failed). 860K legacy
      //   cart-abandons live here; matches the legacy admin's behaviour.
      //   Regular = everything else. "Pēcapmaksa ar bankas pārskaitījumu"
      //   orders are confirmed real orders that stay payment-pending until
      //   staff mark them paid — they belong here and must be fulfillable.
      // Archived orders (testing-period cleanup, staff "hide" action) stay in
      // the DB but never appear unless explicitly asked for.
      if (!showArchived) {
        query = query.is("archived_at", null);
      }
      if (pendingOnly) {
        query = query.eq("status", "pending").neq("payment_method", "bank_transfer");
      } else {
        if (filterStatus) {
          query = query.eq("status", filterStatus);
        }
        query = query.or("status.neq.pending,payment_method.eq.bank_transfer");
        if (filterPaymentStatus) query = query.eq("payment_status", filterPaymentStatus);
      }

      if (filterFulfilmentStore) {
        query = query.eq("fulfilment_store_id", filterFulfilmentStore);
      }

      const dbSortable = ["created_at", "order_number", "total_amount", "status", "payment_status"];
      query = query.order(dbSortable.includes(sortColumn) ? sortColumn : "created_at", {
        ascending: dbSortable.includes(sortColumn) ? sortDirection === "asc" : false,
      });
      query = query.range(from, to);

      const { data, count, error } = await query;
      if (error) {
        toast({
          title: t("orders.errorLoading", "Error loading orders"),
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      setOrders(data ?? []);
      setTotalCount(count ?? 0);
      if (resetPage) setPage(0);
      setLoading(false);
    },
    [
      page,
      search,
      sortColumn,
      sortDirection,
      filterStatus,
      filterPaymentStatus,
      filterFulfilmentStore,
      pendingOnly,
      t,
      toast,
    ]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortColumn, sortDirection, filterStatus, filterPaymentStatus, filterFulfilmentStore, showArchived]);

  // Load stores once for the fulfilment filter. Only active shops are
  // shown; warehouses (if any get added later) are hidden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("inventory_locations")
        .select("id, name, type, active")
        .eq("type", "shop")
        .eq("active", true)
        .order("position");
      if (cancelled) return;
      setStores(
        ((data ?? []) as Array<{ id: string; name: string }>).map((r) => ({
          id: r.id,
          name: r.name,
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => fetchData(true), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/30" />;
    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0" />
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-xl font-bold text-foreground">
            {pendingOnly
              ? t("orders.pendingTitle", "Pending Orders")
              : t("orders.title", "Orders")}
            {totalCount > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({totalCount.toLocaleString()})
              </span>
            )}
          </h1>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t("orders.searchPlaceholder", "Search by order # or customer...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[280px] pl-8 pr-8 font-body text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {!pendingOnly && (
          <Select
            value={filterStatus}
            onValueChange={(val) => {
              setFilterStatus(val === "__all" ? "" : val);
              setPage(0);
            }}
          >
            <SelectTrigger
              className={cn("h-8 w-[150px] font-body text-sm", filterStatus && "border-primary")}
            >
              <SelectValue placeholder={t("orders.allStatuses", "All statuses")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("orders.allStatuses", "All statuses")}</SelectItem>
              <SelectItem value="pending">{t("orders.statusPending", "Pending")}</SelectItem>
              <SelectItem value="paid">{t("orders.statusPaid", "Paid")}</SelectItem>
              <SelectItem value="ready">{t("orders.statusReady", "Ready")}</SelectItem>
              <SelectItem value="fulfilled">{t("orders.statusFulfilled", "Fulfilled")}</SelectItem>
              <SelectItem value="cancelled">
                {t("orders.statusCancelled", "Cancelled")}
              </SelectItem>
              <SelectItem value="refunded">{t("orders.statusRefunded", "Refunded")}</SelectItem>
            </SelectContent>
          </Select>
          )}

          {!pendingOnly && (
            <Select
              value={filterPaymentStatus}
              onValueChange={(val) => {
                setFilterPaymentStatus(val === "__all" ? "" : val);
                setPage(0);
              }}
            >
              <SelectTrigger
                className={cn(
                  "h-8 w-[170px] font-body text-sm",
                  filterPaymentStatus && "border-primary"
                )}
              >
                <SelectValue placeholder={t("orders.allPayments", "All payment statuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">
                  {t("orders.allPayments", "All payment statuses")}
                </SelectItem>
                <SelectItem value="pending">
                  {t("orders.payPending", "Payment pending")}
                </SelectItem>
                <SelectItem value="paid">{t("orders.payPaid", "Paid")}</SelectItem>
                <SelectItem value="failed">{t("orders.payFailed", "Failed")}</SelectItem>
                <SelectItem value="partially_refunded">
                  {t("orders.payPartiallyRefunded", "Partially refunded")}
                </SelectItem>
                <SelectItem value="refunded">{t("orders.payRefunded", "Refunded")}</SelectItem>
              </SelectContent>
            </Select>
          )}

          <Select
            value={filterFulfilmentStore}
            onValueChange={(val) => {
              setFilterFulfilmentStore(val === "__all" ? "" : val);
              setPage(0);
            }}
          >
            <SelectTrigger
              className={cn(
                "h-8 w-[180px] font-body text-sm",
                filterFulfilmentStore && "border-primary"
              )}
            >
              <SelectValue
                placeholder={t("orders.allStores", "All fulfilment stores")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">
                {t("orders.allStores", "All fulfilment stores")}
              </SelectItem>
              {stores.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Archived orders are hidden by default (testing-period cleanup
              and any order staff choose to hide); this reveals them. */}
          <label className="flex cursor-pointer items-center gap-2 font-body text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setPage(0);
              }}
              className="accent-primary"
            />
            {t("orders.showArchived", "Rādīt arhivētos")}
          </label>

          {(filterStatus ||
            (!pendingOnly && filterPaymentStatus) ||
            filterFulfilmentStore) && (
            <button
              onClick={() => {
                setFilterStatus("");
                if (!pendingOnly) setFilterPaymentStatus("");
                setFilterFulfilmentStore("");
                setPage(0);
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X size={12} /> {t("common.clearFilters", "Clear filters")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {t("common.loading", "Loading...")}
        </div>
      ) : orders.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          {t("orders.empty", "No orders found.")}
        </p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead
                    className="cursor-pointer font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("order_number")}
                  >
                    <div className="flex items-center">
                      {t("orders.colNumber", "Order #")}
                      <SortIcon column="order_number" />
                    </div>
                  </TableHead>
                  <TableHead className="font-bold">{t("orders.colCustomer", "Customer")}</TableHead>
                  <TableHead className="font-bold">{t("orders.colEmail", "Email")}</TableHead>
                  <TableHead className="text-center font-bold">
                    {t("orders.colStatus", "Status")}
                  </TableHead>
                  <TableHead className="text-center font-bold">
                    {t("orders.colPayment", "Payment")}
                  </TableHead>
                  <TableHead className="font-bold">
                    {t("orders.colFulfilment", "Fulfilment")}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("total_amount")}
                  >
                    <div className="flex items-center justify-end">
                      {t("orders.colTotal", "Total")}
                      <SortIcon column="total_amount" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center justify-end">
                      {t("orders.colCreated", "Created")}
                      <SortIcon column="created_at" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/orders/${o.id}`)}
                  >
                    <TableCell className="py-2.5 font-mono text-xs font-medium">
                      {o.order_number}
                      {o.is_b2b_invoice && (
                        <span className="ml-1.5 rounded bg-sky-500/15 px-1.5 py-0.5 font-body text-[10px] font-bold uppercase tracking-wide text-sky-700">
                          B2B
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5">
                      {[o.customer_first_name, o.customer_last_name].filter(Boolean).join(" ") ||
                        "—"}
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {o.customer_email ?? (
                        <span className="text-muted-foreground/50">
                          {o.customer_phone ?? "—"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-center">
                      <StatusBadge status={o.status} t={t} />
                    </TableCell>
                    <TableCell className="py-2.5 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <PaymentBadge status={o.payment_status} t={t} />
                        {(o.payment_method_detail ||
                          o.payment_method === "bank_transfer") && (
                          <span className="text-[11px] text-muted-foreground/70">
                            {o.payment_method === "bank_transfer"
                              ? t("orders.methodBankTransfer", "Pārskaitījums")
                              : o.payment_method_detail}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {o.fulfilment_type === "pickup" ? (
                        <div className="flex flex-col gap-0.5">
                          <span>{t("orders.fulfilmentPickup", "Pickup")}</span>
                          <span className="text-[11px] text-muted-foreground/70">
                            {o.shipping_address?.pickup ??
                              t(
                                "orders.pickupStoreUnknown",
                                "store not recorded"
                              )}
                          </span>
                        </div>
                      ) : (
                        t("orders.fulfilmentCourier", "Courier")
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-right font-medium">
                      €{Number(o.total_amount ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="py-2.5 text-right text-muted-foreground">
                      <span className="font-body text-xs">
                        {new Date(o.created_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <span className="font-body text-xs text-muted-foreground">
                {t("common.showing", "Showing")} {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, totalCount)} {t("common.of", "of")}{" "}
                {totalCount.toLocaleString()}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page === 0}
                  onClick={() => setPage(0)}
                >
                  «
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="px-3 font-body text-xs text-foreground">
                  {t("common.page", "Page")} {page + 1} {t("common.of", "of")} {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRightIcon size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(totalPages - 1)}
                >
                  »
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function PendingOrders() {
  return <Orders pendingOnly />;
}

function StatusBadge({ status, t }: { status: string; t: (k: string, f: string) => string }) {
  const variant: "default" | "secondary" | "outline" | "success" | "info" | "destructive" =
    status === "paid"
      ? "info"
      : status === "ready"
        ? "info"
        : status === "fulfilled"
          ? "success"
          : status === "cancelled" || status === "refunded"
            ? "destructive"
            : "secondary";
  const label =
    status === "pending"
      ? t("orders.statusPending", "Pending")
      : status === "paid"
        ? t("orders.statusPaid", "Paid")
        : status === "ready"
          ? t("orders.statusReady", "Ready")
          : status === "fulfilled"
            ? t("orders.statusFulfilled", "Fulfilled")
            : status === "cancelled"
              ? t("orders.statusCancelled", "Cancelled")
              : status === "refunded"
                ? t("orders.statusRefunded", "Refunded")
                : status;
  return <Badge variant={variant}>{label}</Badge>;
}

function PaymentBadge({ status, t }: { status: string; t: (k: string, f: string) => string }) {
  const variant: "default" | "secondary" | "outline" | "success" | "info" | "destructive" =
    status === "paid"
      ? "success"
      : status === "failed" || status === "refunded"
        ? "destructive"
        : status === "partially_refunded"
          ? "outline"
          : "secondary";
  const label =
    status === "pending"
      ? t("orders.payPending", "Pending")
      : status === "paid"
        ? t("orders.payPaid", "Paid")
        : status === "failed"
          ? t("orders.payFailed", "Failed")
          : status === "partially_refunded"
            ? t("orders.payPartiallyRefunded", "Partially refunded")
            : status === "refunded"
              ? t("orders.payRefunded", "Refunded")
              : status;
  return <Badge variant={variant}>{label}</Badge>;
}
